/**
 * Maps v2 public live-class join API responses to the legacy nested shape
 * consumed by LiveClassJoinMeetingPage.
 *
 * V1 has been decommissioned, so this is no longer bridging two live API
 * versions — it's a permanent internal shape-adapter. LiveClassJoinMeetingPage
 * has a single consumer, so migrating it to the flat v2 fields directly (and
 * deleting this adapter) is a reasonable follow-up, but it is a page-level
 * change outside the scope of this pass.
 */

import {launchMeeting} from "../../features/live-events/utils/launch-meeting";
import {TLiveClassApiResponse} from "../live-events/live-classes";
import type {TCustomerLiveClassBookingResponse} from "../booking";

export const LIVE_EVENT_JOIN_ACTOR_TYPE = {
  COACH: "coach",
  CUSTOMER: "customer",
} as const;

export type V2PublicLiveClassJoinData = {
  token: string | null;
  roomId: string | null;
  videoPlatform: string;
  customRoomLink: string | null;
  dailyCoUrl: string | null;
  actorType: string;
  eventDate: string;
  eventTime: string;
  statusName: string;
  coachFirstName: string;
  coachLastName: string;
  callDuration: number;
  customerName: string | null;
  eventDuration: number;
  eventName: string;
  customersTimezone: string | null;
  coachesTimezone: string | null;
};

type VideoPlatform = TCustomerLiveClassBookingResponse["videoPlatform"];

function normalizeVideoPlatform(platform?: string): VideoPlatform {
  const normalized = platform?.toLowerCase() ?? "";

  if (normalized === "dailyco") return "dailyCo";
  if (normalized === "googlemeet") return "googleMeet";
  if (normalized === "custom") return "custom";
  return "videoSdk";
}

/**
 * Splits a display name into first and last name for videoSdk guest metadata.
 */
function splitCustomerName(customerName: string | null | undefined): {
  firstName: string;
  lastName: string;
} {
  const trimmed = (customerName ?? "").trim();
  const spaceIndex = trimmed.indexOf(" ");

  if (spaceIndex === -1) {
    return {firstName: trimmed, lastName: ""};
  }

  return {
    firstName: trimmed.slice(0, spaceIndex),
    lastName: trimmed.slice(spaceIndex + 1).trim(),
  };
}

function createEventStub(
  v2Data: V2PublicLiveClassJoinData,
  videoPlatform: VideoPlatform
): TLiveClassApiResponse {
  return {
    activeSession: {
      name: v2Data.eventName,
      startDate: v2Data.eventDate,
      startTime: v2Data.eventTime,
      duration: v2Data.eventDuration,
      endTime: "",
    },
    activeSessionId: 0,
    attendees: 0,
    bookingQuestions: [],
    callPrice: 0,
    callPriceCurrency: "",
    cover_image: "",
    createdAt: new Date(0),
    currencies: [],
    description: "",
    hide_from_menu: false,
    id: 0,
    name: v2Data.eventName,
    reference: "",
    roomId: v2Data.roomId ?? "",
    tiny_url: "",
    session_count: 0,
    session_info: [],
    slug: "",
    statusId: 1,
    statusName: "Active",
    topic_type: "single",
    request_phone_number: false,
    updatedAt: new Date(0),
    userId: 0,
    is_custom_room: Boolean(v2Data.customRoomLink),
    video_platform: videoPlatform,
  };
}

function createEventBookingStub(
  v2Data: V2PublicLiveClassJoinData,
  roomId: string | null,
  customerName: {firstName: string; lastName: string}
): TCustomerLiveClassBookingResponse["eventBooking"] {
  const eventDate = new Date(v2Data.eventDate);

  return {
    id: 0,
    userId: 0,
    eventId: 0,
    paymentProvider: "",
    roomId,
    current_session: 0,
    firstName: customerName.firstName,
    lastName: customerName.lastName,
    emailAddress: "",
    bookingQuestions: "[]",
    is_series: false,
    is_paid: false,
    event_date: eventDate,
    reference: "",
    call_duration: v2Data.callDuration,
    coach_event_time: v2Data.eventTime,
    coach_end_time: "",
    customer_event_time: v2Data.eventTime,
    customer_end_time: "",
    customer_timezone: v2Data.customersTimezone ?? "",
    statusId: 1,
    createdAt: eventDate,
    updatedAt: eventDate,
    startDate: eventDate,
    startTime: v2Data.eventTime,
    statusName: v2Data.statusName,
    endTime: "",
  };
}

type ResolveLiveClassJoinDisplayTimezoneParams = {
  booking: Pick<
    TCustomerLiveClassBookingResponse,
    "actorType" | "customersTimezone" | "coachesTimezone" | "eventBooking"
  >;
  browserTimezone: string;
};

/**
 * Returns the timezone label shown beside the globe icon on the public join page.
 * Uses coach timezone for coach actors and customer timezone for customer actors.
 */
export function resolveLiveClassJoinDisplayTimezone({
  booking,
  browserTimezone,
}: ResolveLiveClassJoinDisplayTimezoneParams): string {
  const actorType = booking.actorType?.toLowerCase();
  const eventBookingTimezone = booking.eventBooking.customer_timezone.trim();

  if (actorType === LIVE_EVENT_JOIN_ACTOR_TYPE.COACH) {
    return (
      booking.coachesTimezone ?? (eventBookingTimezone || undefined) ?? browserTimezone
    );
  }

  if (actorType === LIVE_EVENT_JOIN_ACTOR_TYPE.CUSTOMER) {
    return (
      booking.customersTimezone ?? (eventBookingTimezone || undefined) ?? browserTimezone
    );
  }

  return eventBookingTimezone || browserTimezone;
}

/**
 * Normalizes the flat v2 public live-class join payload into the legacy nested
 * response shape consumed by LiveClassJoinMeetingPage.
 */
export function normalizeV2PublicLiveClassJoinResponse(
  v2Data: V2PublicLiveClassJoinData
): TCustomerLiveClassBookingResponse {
  const videoPlatform = normalizeVideoPlatform(v2Data.videoPlatform);
  const roomId = v2Data.roomId ?? v2Data.customRoomLink ?? null;
  const customerName = splitCustomerName(v2Data.customerName);

  return {
    bookingToken: v2Data.token,
    videoPlatform,
    dailyCoUrl: v2Data.dailyCoUrl ?? "",
    customRoomLink: v2Data.customRoomLink,
    actorType: v2Data.actorType,
    customersTimezone: v2Data.customersTimezone,
    coachesTimezone: v2Data.coachesTimezone,
    coach: {
      firstName: v2Data.coachFirstName,
      lastName: v2Data.coachLastName,
    },
    event: createEventStub(v2Data, videoPlatform),
    eventBooking: createEventBookingStub(v2Data, roomId, customerName),
  };
}

/**
 * Returns whether the public live-class join button should be disabled.
 * Custom rooms join via external links and do not require a booking token.
 */
export function isLiveClassJoinButtonDisabled(
  booking: TCustomerLiveClassBookingResponse
): boolean {
  const platform = booking.videoPlatform.toLowerCase();

  if (platform === "custom") {
    return !(booking.customRoomLink ?? booking.eventBooking.roomId);
  }

  if (!booking.bookingToken) {
    return true;
  }

  if (platform === "googlemeet" && !booking.eventBooking.roomId) {
    return true;
  }

  return false;
}

/**
 * Message shown below the join button when joining is not yet available.
 */
export function getLiveClassJoinDisabledMessage(
  booking: TCustomerLiveClassBookingResponse
): string {
  const platform = booking.videoPlatform.toLowerCase();
  const hasMeetingLink = Boolean(booking.customRoomLink ?? booking.eventBooking.roomId);

  if ((platform === "googlemeet" || platform === "custom") && !hasMeetingLink) {
    return "Meeting link is being prepared, please wait...";
  }

  return "You will be able to join this video call 10 minutes before the start time";
}

/**
 * Composes the guest join URL for the public live-class join page.
 */
export function resolveLiveClassJoinMeetingUrl(
  booking: TCustomerLiveClassBookingResponse
): string {
  return launchMeeting({
    joinResult: {
      token: booking.bookingToken ?? "",
      roomId: booking.eventBooking.roomId ?? "",
      videoPlatform: booking.videoPlatform,
      customRoomLink: booking.customRoomLink ?? null,
      dailyCoUrl: booking.dailyCoUrl || null,
    },
    user: {
      firstName: booking.eventBooking.firstName,
      lastName: booking.eventBooking.lastName,
    },
    event: {name: booking.event.name},
    role: "guest",
  }).url;
}
